const MAX_DIMENSION = 1920
const QUALITY = 0.8

/**
 * Compress and resize an image blob.
 * - Caps the longest side at 1920px (preserves aspect ratio)
 * - Exports as WebP at 80% quality (JPEG fallback)
 * - GIFs pass through unmodified to preserve animation
 */
export async function compressImage(blob: Blob): Promise<Blob> {
  // Pass through GIFs to preserve animation
  if (blob.type === 'image/gif') {
    return blob
  }

  // Load image into an HTMLImageElement
  const img = await loadImage(blob)

  // Calculate target dimensions
  const { width, height } = calculateDimensions(img.naturalWidth, img.naturalHeight)

  // If already small enough and already a web-friendly format, skip compression
  if (
    width === img.naturalWidth &&
    height === img.naturalHeight &&
    blob.size < 200_000 // under 200KB
  ) {
    return blob
  }

  // Draw onto off-screen canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, width, height)

  // Try WebP first, fall back to JPEG
  let compressed = await canvasToBlob(canvas, 'image/webp', QUALITY)
  if (!compressed || compressed.size === 0) {
    compressed = await canvasToBlob(canvas, 'image/jpeg', QUALITY)
  }

  return compressed || blob
}

/**
 * Get the MIME type of the compressed output for a given input type.
 */
export function getCompressedMimeType(originalType: string): string {
  if (originalType === 'image/gif') return 'image/gif'
  // We compress to WebP (with JPEG fallback, but report WebP as default)
  return 'image/webp'
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

function calculateDimensions(
  origWidth: number,
  origHeight: number
): { width: number; height: number } {
  if (origWidth <= MAX_DIMENSION && origHeight <= MAX_DIMENSION) {
    return { width: origWidth, height: origHeight }
  }

  const ratio = Math.min(MAX_DIMENSION / origWidth, MAX_DIMENSION / origHeight)
  return {
    width: Math.round(origWidth * ratio),
    height: Math.round(origHeight * ratio),
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      type,
      quality
    )
  })
}
